import React from 'react'
import styled from '@emotion/styled'

import { useParams } from 'react-router-dom'
import {
    Box,
    Button,
    ButtonProps,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    MenuItemProps,
    Fab,
    Toolbar,
} from '@mui/material'
import {
    Add as AddIcon,
    Dashboard as DashboardIcon,
    Delete as DeleteIcon,
    LibraryAdd as LibraryAddIcon,
    Settings as SettingsIcon,
    Info as InfoIcon,
} from '@mui/icons-material'

import { ConfirmDialog, useIsMobile } from '@edge/spark/ui-mui'
import { useSearchParams } from '@edge/spark/utils'
import { AddDashboardButton } from '../AddDashboardButton'
import { CopyDashboardDialog } from '../CopyDashboardDialog'

import { generateLayoutsForGrid } from '../Dashboard/DashboardLayout'
import {
    useSaveLayoutsMutation,
    useCreateDashboardMutation,
    useDeleteDashboardMutation,
    DASHBOARD_LIMIT,
    type DashboardLayout,
    useIsEditingLayouts,
    setTempLayouts,
    setIsEditingLayouts,
    openAccessoryDrawer,
} from '@edge/spark/io-dashboards'

const StyledListItemIcon = styled(ListItemIcon)`
    min-width: 40px;
`

interface Props {
    activeDashboard: DashboardLayout
    cancelEdits: () => void
    dashboards: DashboardLayout[]
    disabled?: boolean
}

export const DashboardCrud: React.FC<Props> = ({ activeDashboard, cancelEdits, dashboards, disabled }: Props) => {
    const { uid: userId } = useSearchParams()
    const { dashId } = useParams<{ dashId?: string }>()

    const [confirmOpen, setConfirmOpen] = React.useState(false)
    const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false)
    const [copyDialogOpen, setCopyDialogOpen] = React.useState(false)

    const [isDeletingDashboard, setIsDeletingDashboard] = React.useState(false)
    const [anchorEl, setAnchorEl] = React.useState<Element>()
    const mobile = useIsMobile()
    const [deleteDash] = useDeleteDashboardMutation()

    const [createDashboard] = useCreateDashboardMutation()
    const [doSaveLayouts] = useSaveLayoutsMutation()
    const isEditingLayouts = useIsEditingLayouts()

    const closeMobileMenu = (): void => setAnchorEl(undefined)
    // const closeMenu = () => endEditingLayouts()

    const handleCloseMenu: MenuItemProps['onClick'] = () => closeMobileMenu()

    const handleOpenMenu: ButtonProps['onClick'] = (e) => setAnchorEl(e.currentTarget)

    const handleClickEditDashboard = async () => {
        closeMobileMenu()
        const layout = generateLayoutsForGrid(activeDashboard.cards, 6)
        setTempLayouts(JSON.stringify(layout))
        setIsEditingLayouts(true)
    }

    const handleClickCreateDashboard = (): void => {
        closeMobileMenu()
        createDashboard({ variables: { userId, name: 'Dashboard' } })
        openAccessoryDrawer()
    }

    const handleClickDeleteCurrentDashboard = (): void => {
        setIsDeletingDashboard(true)
        setConfirmOpen(true)
    }

    const handleClickCreateCard = (): void => {
        closeMobileMenu()
        openAccessoryDrawer()
    }

    const handleCancelEditingLayouts = (): void => {
        setCancelConfirmOpen(true)
    }
    const handleConfirmCancelEdits = (): void => {
        cancelEdits()
        setIsEditingLayouts(false)
        setCancelConfirmOpen(false)
    }

    const handleConfirmFailure = (): void => {
        setConfirmOpen(false)
        setIsEditingLayouts(false)
    }
    const handleKeepEditingLayouts = (): void => {
        setCancelConfirmOpen(false)
    }

    const handleConfirmSuccess = async (): Promise<void> => {
        setConfirmOpen(false)
        if (isDeletingDashboard) {
            await deleteDash({ variables: { userId, dashId } })
            setIsDeletingDashboard(false)
        }

        if (isEditingLayouts) {
            setTimeout(() => {
                setIsEditingLayouts(false)
            }, 0)
            cancelEdits()
        }
    }

    const handleSaveEditingLayouts = (): void => {
        doSaveLayouts({ variables: { dashId, userId } })
    }

    const handleClickCopyCurrentDashboard = (): void => {
        setCopyDialogOpen(true)
        setIsEditingLayouts(false)
    }
    const handleCancelCopyCurrentDashboard = (): void => setCopyDialogOpen(false)

    const disableAddDashboard = disabled || dashboards.length >= DASHBOARD_LIMIT
    const disableSettings = disabled || !dashboards

    const AddCardButton = (): JSX.Element => (
        <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={handleClickCreateCard}
            startIcon={<AddIcon />}
            sx={{ mr: 0.8 }}
            disabled={disabled}
        >
            Card
        </Button>
    )

    return (
        <>
            <ConfirmDialog
                open={confirmOpen}
                postMessageEm={activeDashboard.name}
                onSuccess={handleConfirmSuccess}
                onFailure={handleConfirmFailure}
                title="Delete"
                icon={<DeleteIcon />}
                message="Are you sure you want to delete?"
                successButtonText="OK"
                failureButtonText="Cancel"
            />
            <ConfirmDialog
                open={cancelConfirmOpen}
                postMessageEm={activeDashboard.name}
                onSuccess={handleConfirmCancelEdits}
                onFailure={handleKeepEditingLayouts}
                title="Cancel"
                icon={<InfoIcon />}
                message="Are you sure you want to cancel editing?"
                successButtonText="Yes"
                failureButtonText="No"
            />
            <CopyDashboardDialog open={copyDialogOpen} onClose={handleCancelCopyCurrentDashboard} />
            {isEditingLayouts ? (
                <Toolbar disableGutters sx={{ width: '100%' }}>
                    {dashboards.length > 1 && (
                        // <Tooltip
                        //   enterDelay={TOOLTIP_DELAY}
                        //   title={activeDashboard.id}
                        //   aria-label="delete"
                        // >
                        <div>
                            <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{
                                    mr: 0.8,
                                    whiteSpace: 'nowrap',
                                    display: { xs: 'none', md: 'block' },
                                }}
                                onClick={handleClickDeleteCurrentDashboard}
                                disabled={disabled}
                            >
                                Delete Dashboard
                            </Button>
                            <IconButton
                                sx={{
                                    mr: 0.8,
                                    display: { md: 'none' },
                                }}
                                onClick={handleClickDeleteCurrentDashboard}
                                disabled={disabled}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </div>
                        // </Tooltip>
                    )}
                    <Button
                        color="primary"
                        sx={{ mr: 0.8, width: '100%' }}
                        variant="outlined"
                        onClick={handleClickCopyCurrentDashboard}
                        size="small"
                        disabled={disabled}
                    >
                        Copy
                    </Button>
                    <Button
                        color="primary"
                        sx={{ mr: 0.8, width: '100%' }}
                        variant="outlined"
                        onClick={handleSaveEditingLayouts}
                        size="small"
                        disabled={disabled}
                    >
                        Save
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ mr: 0.8, width: '100%' }}
                        onClick={handleCancelEditingLayouts}
                    >
                        Cancel
                    </Button>
                </Toolbar>
            ) : (
                <div>
                    <div>
                        <Box
                            component="span"
                            sx={{
                                '& > *': { m: 1 },
                                display: { xs: 'none', md: 'unset' },
                            }}
                        >
                            <span>
                                <AddDashboardButton disabled={disableAddDashboard} />
                            </span>
                            <AddCardButton />
                        </Box>
                        <Fab
                            sx={{
                                position: 'fixed',
                                zIndex: 99,
                                bottom: 9,
                                right: 3,
                                display: { sm: 'none' },
                            }}
                            color="primary"
                            onClick={handleClickCreateCard}
                            disabled={disabled}
                        >
                            <AddIcon />
                        </Fab>
                        <IconButton
                            aria-controls="simple-menu"
                            size="small"
                            aria-haspopup="true"
                            onClick={!mobile ? handleClickEditDashboard : handleOpenMenu}
                            disabled={disableSettings}
                            edge="end"
                        >
                            <SettingsIcon />
                        </IconButton>
                    </div>
                    <Menu
                        id="simple-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={!!anchorEl}
                        onClose={handleCloseMenu}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    >
                        <MenuItem onClick={handleClickEditDashboard}>
                            <StyledListItemIcon>
                                <DashboardIcon />
                            </StyledListItemIcon>
                            <ListItemText>Edit Dashboard</ListItemText>
                        </MenuItem>

                        <MenuItem disabled={disableAddDashboard} onClick={handleClickCreateDashboard}>
                            <StyledListItemIcon>
                                <LibraryAddIcon />
                            </StyledListItemIcon>
                            <ListItemText>{`Add Dashboard${
                                dashboards.length >= DASHBOARD_LIMIT ? ` (Limit ${DASHBOARD_LIMIT})` : ''
                            }`}</ListItemText>
                        </MenuItem>
                    </Menu>
                </div>
            )}
        </>
    )
}
